import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/client/lib/api';
import { Layers, ChevronDown, Check, X } from 'lucide-react';
import { Button } from '@/client/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/client/components/ui/dropdown-menu';
import { Badge } from '@/client/components/ui/badge';

interface Project {
  id: string;
  name: string;
  color?: string;
}

interface ProjectFilterProps {
  selectedProjectIds: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
}

export function ProjectFilter({ selectedProjectIds, onChange, disabled }: ProjectFilterProps) {
  const { data: projectsData, isLoading } = useQuery({
    queryKey: ['projects'],
    queryFn: async () => {
      const response = await apiFetch('/api/projects');
      if (!response.ok) return { data: [] };
      return response.json();
    },
  });

  const projects = projectsData?.data || [];

  const toggleProject = (id: string) => {
    if (selectedProjectIds.includes(id)) {
      onChange(selectedProjectIds.filter((pid) => pid !== id));
    } else {
      onChange([...selectedProjectIds, id]);
    }
  };

  const hasSelection = selectedProjectIds.length > 0;

  if (projects.length === 0 && !isLoading) return null;

  return (
    <div className="flex items-center gap-2">
      <DropdownMenu>
        <DropdownMenuTrigger asChild disabled={disabled || isLoading}>
          <Button
            variant={hasSelection ? 'default' : 'outline'}
            size="sm"
            className="rounded-xl gap-1.5 h-8"
          >
            <Layers className="size-3.5" />
            Projects
            {hasSelection && (
              <Badge variant="secondary" className="ml-1 px-1.5 py-0 text-xs">
                {selectedProjectIds.length}
              </Badge>
            )}
            <ChevronDown className="size-3.5 ml-1" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-56 max-h-64 overflow-y-auto">
          {projects.map((project: Project) => (
            <DropdownMenuItem
              key={project.id}
              onClick={() => toggleProject(project.id)}
              className="flex items-center justify-between cursor-pointer"
            >
              <div className="flex items-center gap-2 truncate">
                <div
                  className="w-2 h-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: project.color }}
                />
                <span className="truncate">{project.name}</span>
              </div>
              {selectedProjectIds.includes(project.id) && <Check className="size-4 text-primary" />}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {hasSelection && (
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={() => onChange([])}
          className="h-8 w-8 rounded-full"
          title="Clear project filter"
        >
          <X className="size-3.5" />
        </Button>
      )}
    </div>
  );
}
